'use client';

import {
  faEarthAmerica,
  faGraduationCap,
  faIdBadge,
  faNoteMedical,
} from '@fortawesome/pro-light-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';
import {
  EntityRatingCommentCount,
  Professional,
  ProfessionalSchedule,
  Rating,
} from '@medprev-web-app/api-graphql-client';

import { ProfessionalAbout } from './professional-about.component';
import { ProfessionalServiceLocations } from './professional-service-locations.component';

import { EntitySection } from '@/app/(main)/(pages)/_components/entities/entity-section.component';
import {
  EntityTab,
  EntityTabContent,
  EntityTabList,
  EntityTabProvider,
  EntityTabTrigger,
} from '@/app/(main)/(pages)/_components/entities/entity-tab.component';
import { RatingCard } from '@/app/(main)/(pages)/_components/entities/entity-rating-card.component';
import { ItemList } from '@/app/(main)/(pages)/_components/entities/item-list.component';
import { formatExperiences } from '@/utils/professional/professional-license.util';
import { RatingComment } from '@/types';
import { EntityLoadMoreComments } from '@/app/(main)/(pages)/_components/entities/entity-load-more-comments.component';
import {
  formatProfessionalFormations,
  formatProfessionalLanguages,
  formatProfessionalLicenses,
  formatProfessionalTreatments,
} from '@/utils/professional/online-professional.utils';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';

interface TabProfessionalProps {
  professional: Professional;
  countComments?: EntityRatingCommentCount | null;
  inPersonData: ProfessionalSchedule[];
  telehealthData: ProfessionalSchedule[];
  initialComments: RatingComment[];
}

export function TabProfessional({
  professional,
  countComments,
  inPersonData,
  telehealthData,
  initialComments,
}: TabProfessionalProps) {
  const formattedFormations = formatProfessionalFormations(professional);

  const formattedExperiences = formatExperiences(professional);

  const formationAndExperiences = [
    ...formattedFormations,
    ...formattedExperiences,
  ];

  const formattedLanguages = formatProfessionalLanguages(professional);

  const formattedTreatments = formatProfessionalTreatments(professional);

  const formattedLicenses = formatProfessionalLicenses(professional);

  return (
    <>
      <EntityTabProvider>
        <EntityTab
          defaultValue="about"
          className="mt-5"
          tabs={['about', 'rating']}
        >
          <EntityTabList className="sticky top-0 bg-white pt-5">
            <EntityTabTrigger
              data-testid={DataTestIdEnum.PROFESSIONAL_PROFILE_TAB_ABOUT}
              value="about"
              data-analytics-event={AnalyticsEnum.PROFESSIONAL_PROFILE_CLICK_ABOUT_TAB}
            >
              Sobre
            </EntityTabTrigger>
            <EntityTabTrigger
              data-testid={DataTestIdEnum.PROFESSIONAL_PROFILE_TAB_RATING}
              value="rating"
              data-analytics-event={AnalyticsEnum.PROFESSIONAL_PROFILE_CLICK_REVIEWS_TAB}
            >
              Avaliações
            </EntityTabTrigger>
          </EntityTabList>

          <div
            className="mt-4"
            data-testid={DataTestIdEnum.PROFESSIONAL_PROFILE_TABS_CONTAINER}
          >
            <EntityTabContent value="about">
              {telehealthData.length > 0
                || (inPersonData.length > 0 && (
                  <ProfessionalServiceLocations
                    professional={professional}
                    telehealthData={telehealthData}
                    inPersonData={inPersonData}
                  />
                ))}

              {professional.description && (
                <EntitySection title="Sobre mim" id="about-me">
                  <ProfessionalAbout professional={professional} />
                </EntitySection>
              )}

              {formattedLicenses.length > 0 && (
                <EntitySection
                  icon={faIdBadge}
                  title={
                    formattedLicenses.length > 1 ? 'Registros' : 'Registro'
                  }
                  classNameIcon="mt-1"
                >
                  <ItemList
                    items={formattedLicenses}
                    listType="none"
                    dataTestID="licenses"
                    data-analytics-event={AnalyticsEnum.PROFESSIONAL_PROFILE_EXPAND_LICENSES}
                  />
                </EntitySection>
              )}

              {formationAndExperiences.length > 0 && (
                <EntitySection
                  icon={faGraduationCap}
                  title="Formação & experiência"
                  classNameIcon="mt-1"
                >
                  <ItemList
                    items={formationAndExperiences}
                    dataTestID="formations"
                    data-analytics-event={AnalyticsEnum.PROFESSIONAL_PROFILE_EXPAND_EDUCATION_AND_EXPERIENCE}
                  />
                </EntitySection>
              )}

              {formattedTreatments.length > 0 && (
                <EntitySection
                  icon={faNoteMedical}
                  title="Doenças, sintomas ou condições"
                  classNameIcon="mt-1"
                >
                  <ItemList
                    items={formattedTreatments}
                    dataTestID="treatments"
                    data-analytics-event={AnalyticsEnum.PROFESSIONAL_PROFILE_EXPAND_DISEASES}
                  />
                </EntitySection>
              )}

              {formattedLanguages.length > 0 && (
                <EntitySection
                  icon={faEarthAmerica}
                  title={formattedLanguages.length > 1 ? 'Idiomas' : 'Idioma'}
                  classNameIcon="mt-1"
                >
                  <ItemList
                    items={formattedLanguages}
                    dataTestID="languages"
                    data-analytics-event={AnalyticsEnum.PROFESSIONAL_PROFILE_EXPAND_LANGUAGES}
                  />
                </EntitySection>
              )}
            </EntityTabContent>

            <EntityTabContent value="rating">
              <EntitySection removeHr={countComments?.total === 0}>
                <RatingCard
                  rating={professional.rating as Rating}
                  countComments={countComments}
                />
              </EntitySection>

              {countComments && Number(countComments?.total) > 0 && (
                <EntityLoadMoreComments
                  countComments={countComments}
                  professionalId={professional.id as string}
                  initialComments={initialComments}
                  analyticsEventShowMore={AnalyticsEnum.PROFESSIONAL_PROFILE_EXPAND_COMMENT}
                  analyticsEventShowMoreComments={AnalyticsEnum.PROFESSIONAL_PROFILE_CLICK_SHOW_MORE_COMMENTS}
                />
              )}
            </EntityTabContent>
          </div>
        </EntityTab>
      </EntityTabProvider>
    </>
  );
}
